'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import type { Motion } from './GradientShader';
import { CARD_SHADER, textureById } from './gradientTextures';
import { useHoverShaders } from './useHoverShaders';
import { useReveal } from './useReveal';
import styles from './ValidatedMetrics.module.css';

const GradientShader = dynamic(() => import('./GradientShader'), {
  ssr: false,
});

/* On hover (desktop only) each stat card fades in the same animated Dither
   Gradient as the Selected research cards, from the DARK half of the set, and
   its copy flips to white. Each card gets its own colour, its own shape
   (sweep, band, rise, wave) and its own motion, so no two look or move alike. */
const CARD_GRADIENTS: { image: string; motion: Motion; reverse?: boolean }[] = [
  { image: '16', motion: 'waveform' }, // blue corner sweep
  { image: '10', motion: 'flow' }, // copper diagonal band on charcoal
  { image: '28', motion: 'waveform', reverse: true }, // soft violet rise; the 8 card's wave, run backwards
  { image: '30', motion: 'ripple' }, // ember wave
];

type ValidatedMetricsProps = {
  /** live white-paper count from the CMS; the copy value is the fallback */
  whitePapers?: number;
};

export default function ValidatedMetrics({
  whitePapers,
}: ValidatedMetricsProps) {
  const t = useTranslations('science');
  /* eyebrow + heading, then the four stat cards, fade up as they enter (the
     same gentle fade as the rest of the page); the numbers render as-is, no
     count-up — the hover gradient is this section's one animation */
  const revealRef = useReveal<HTMLDivElement>();
  const hoverShaders = useHoverShaders();
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  // a card's shader mounts on its first hover and stays mounted (fades on leave)
  const [armed, setArmed] = useState<readonly boolean[]>([
    false,
    false,
    false,
    false,
  ]);

  const stats = [
    {
      num: t('science.landing.stats.publications.value'),
      label: t('science.landing.stats.publications.label'),
      note: t('science.landing.stats.publications.body'),
    },
    {
      num: whitePapers
        ? String(whitePapers)
        : t('science.landing.stats.whitePapers.value'),
      label: t('science.landing.stats.whitePapers.label'),
      note: t('science.landing.stats.whitePapers.body'),
    },
    {
      num: t('science.landing.stats.collaborations.value'),
      label: t('science.landing.stats.collaborations.label'),
      note: t('science.landing.stats.collaborations.body'),
    },
    {
      num: t('science.landing.stats.algorithms.value'),
      label: t('science.landing.stats.algorithms.label'),
      note: t('science.landing.stats.algorithms.body'),
    },
  ];

  return (
    <section className={styles.section} aria-labelledby="science-stats-title">
      <div ref={revealRef} className={styles.inner}>
        <header className={styles.head}>
          <p className={styles.eyebrow} data-reveal>
            {t('science.landing.stats.eyebrow')}
          </p>
          <h2 id="science-stats-title" className={styles.heading} data-reveal>
            {t('science.landing.stats.title')}
          </h2>
        </header>
        <div className={styles.grid}>
          {stats.map((s, i) => {
            const g = CARD_GRADIENTS[i % CARD_GRADIENTS.length]!;
            const enter = () => {
              if (!hoverShaders) return;
              setHoverIdx(i);
              setArmed((prev) =>
                prev[i] ? prev : prev.map((v, j) => (j === i ? true : v)),
              );
            };
            const leave = () => setHoverIdx((v) => (v === i ? null : v));
            return (
              <article
                key={s.label}
                className={styles.card}
                data-reveal
                data-on={hoverIdx === i ? 'true' : undefined}
                onMouseEnter={enter}
                onMouseLeave={leave}
              >
                {armed[i] ? (
                  <span className={styles.shader} aria-hidden="true">
                    <GradientShader
                      className={styles.gradient}
                      src={textureById(g.image).src}
                      seed={i * 2.3 + 0.7}
                      params={{
                        ...CARD_SHADER,
                        motion: g.motion,
                        speed: g.reverse
                          ? -CARD_SHADER.speed
                          : CARD_SHADER.speed,
                      }}
                    />
                  </span>
                ) : null}
                <div className={styles.top}>
                  <span className={styles.num}>{s.num}</span>
                  <span className={styles.label}>{s.label}</span>
                </div>
                <div className={styles.note}>
                  <span className={styles.bullet} aria-hidden="true" />
                  <p className={styles.noteText}>{s.note}</p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
